/**
 * Workforce Management — presentational kit shared by the planner and the
 * agent self-service pages.
 *
 * Design language: soft slate surfaces, 3px-radius cards with a hairline
 * ring and a whisper of shadow, generous spacing, one brand accent
 * (#3366FF), pill-shaped status and segmented controls, tabular numerals for
 * every figure. Colour is used for meaning (status, activity), never for
 * decoration.
 */
import { useEffect, type JSX, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, Loader2, Search, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { prettyStatus } from './wfm-shared.js'

export const CARD =
  'rounded-[3px] bg-white ring-1 ring-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_4px_12px_-6px_rgba(15,23,42,0.06)]'
export const INPUT =
  'h-9 w-full rounded-[3px] border-0 bg-white px-3 text-[13px] text-slate-800 ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 transition-shadow focus:outline-none focus:ring-2 focus:ring-[#3366FF]/60 disabled:bg-slate-50 disabled:text-slate-400 [&[type=color]]:p-1'
export const LABEL = 'text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400'

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export function WfmPage({
  title,
  subtitle,
  Icon,
  actions,
  eyebrow = 'Workforce',
  children,
}: {
  title: string
  subtitle?: string | undefined
  Icon?: LucideIcon | undefined
  actions?: ReactNode | undefined
  eyebrow?: string | undefined
  children: ReactNode
}): JSX.Element {
  return (
    <div className="min-h-full bg-[linear-gradient(180deg,#F8FAFD_0%,#F3F6FB_100%)] px-6 py-6">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex items-center gap-3.5">
            {Icon && (
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[3px] bg-blue-50 text-[#3366FF]">
                <Icon className="h-5 w-5" />
              </span>
            )}
            <div className="min-w-0">
              {eyebrow && <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#3366FF]/80">{eyebrow}</p>}
              <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.01em] text-slate-900">{title}</h1>
              {subtitle && <p className="mt-1 max-w-3xl text-[13px] leading-relaxed text-slate-500">{subtitle}</p>}
            </div>
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
        {children}
      </div>
    </div>
  )
}

export function Panel({
  title,
  subtitle,
  actions,
  children,
  padded = true,
  Icon,
}: {
  title?: string | undefined
  subtitle?: string | undefined
  actions?: ReactNode | undefined
  children: ReactNode
  padded?: boolean | undefined
  Icon?: LucideIcon | undefined
}): JSX.Element {
  return (
    <section className={`${CARD} flex flex-col overflow-hidden`}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-3 px-5 pb-3 pt-4">
          <div className="flex min-w-0 items-center gap-2.5">
            {Icon && (
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] bg-blue-50 text-[#3366FF]">
                <Icon className="h-4 w-4" />
              </span>
            )}
            <div className="min-w-0">
              {title && <h3 className="truncate text-[14px] font-semibold text-slate-900">{title}</h3>}
              {subtitle && <p className="mt-0.5 truncate text-[12px] text-slate-500">{subtitle}</p>}
            </div>
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={padded ? 'px-5 pb-5' : ''}>{children}</div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export type StatTone = 'default' | 'good' | 'warning' | 'critical' | 'brand'

const TONE: Record<StatTone, { icon: string; dot: string; bar: string }> = {
  default: { icon: 'text-slate-300', dot: 'bg-slate-400', bar: 'bg-[#3366FF]' },
  brand: { icon: 'text-[#3366FF]', dot: 'bg-[#3366FF]', bar: 'bg-[#3366FF]' },
  good: { icon: 'text-emerald-500', dot: 'bg-emerald-500', bar: 'bg-emerald-500' },
  warning: { icon: 'text-amber-500', dot: 'bg-amber-500', bar: 'bg-amber-500' },
  critical: { icon: 'text-rose-500', dot: 'bg-rose-500', bar: 'bg-rose-500' },
}

export function StatGrid({ children }: { children: ReactNode }): JSX.Element {
  return <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">{children}</div>
}

export function StatCard({
  label,
  value,
  hint,
  Icon,
  tone = 'default',
  progress,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode | undefined
  Icon?: LucideIcon | undefined
  tone?: StatTone | undefined
  /** 0–100: thin bar under the value. */
  progress?: number | null | undefined
}): JSX.Element {
  const t = TONE[tone]
  return (
    <div className={`${CARD} p-4 transition hover:shadow-md`}>
      <div className="flex items-center justify-between">
        <span className="flex min-w-0 items-center gap-2">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${t.dot}`} />
          <span className="truncate text-[11px] font-bold uppercase tracking-wider text-slate-600">{label}</span>
        </span>
        {Icon && <Icon className={`h-4 w-4 shrink-0 ${t.icon}`} />}
      </div>
      <p className="mt-3 font-mono text-2xl font-extrabold leading-none tracking-tight tabular-nums text-slate-900">{value}</p>
      {hint !== undefined && <p className="mt-2 truncate text-[11px] leading-snug text-slate-400">{hint}</p>}
      {progress !== undefined && progress !== null && (
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full ${t.bar}`} style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
        </div>
      )}
    </div>
  )
}

/** Circular progress ring with the value in the middle. */
export function Ring({
  value,
  target,
  size = 132,
  stroke = 12,
  label,
}: {
  value: number | null
  target?: number | undefined
  size?: number | undefined
  stroke?: number | undefined
  label?: string | undefined
}): JSX.Element {
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const pct = value === null ? 0 : Math.max(0, Math.min(100, value))
  const color = value === null ? '#CBD5E1' : target === undefined || pct >= target ? '#10B981' : pct >= target - 10 ? '#F59E0B' : '#F43F5E'
  const tAngle = target !== undefined ? (target / 100) * 2 * Math.PI - Math.PI / 2 : null
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EEF2F7" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} style={{ transition: 'stroke-dashoffset 600ms ease' }}
        />
      </svg>
      {tAngle !== null && (
        <span
          className="absolute h-3.5 w-[3px] rounded-full bg-slate-700"
          style={{
            left: size / 2 + r * Math.cos(tAngle) - 1.5,
            top: size / 2 + r * Math.sin(tAngle) - 7,
            transform: `rotate(${tAngle + Math.PI / 2}rad)`,
          }}
          title={`Target ${target}%`}
        />
      )}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[28px] font-semibold leading-none tracking-[-0.02em] tabular-nums text-slate-900">{value === null ? '—' : `${Math.round(value * 10) / 10}%`}</span>
        {label && <span className="mt-1 text-[11px] font-medium text-slate-400">{label}</span>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

type BtnVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'success'

const BTN: Record<BtnVariant, string> = {
  primary: 'bg-[#3366FF] text-white shadow-[0_1px_2px_rgba(15,23,42,0.08),0_4px_12px_-4px_rgba(51,102,255,0.5)] hover:bg-[#2B57E0]',
  secondary: 'bg-white text-slate-700 ring-1 ring-inset ring-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:bg-slate-50',
  danger: 'bg-white text-rose-600 ring-1 ring-inset ring-rose-200 hover:bg-rose-50',
  success: 'bg-emerald-600 text-white shadow-[0_4px_12px_-4px_rgba(16,185,129,0.55)] hover:bg-emerald-700',
  ghost: 'bg-transparent text-slate-600 hover:bg-slate-100',
}

export function Btn({
  children,
  onClick,
  variant = 'secondary',
  Icon,
  disabled,
  loading,
  type = 'button',
  size = 'md',
  title,
}: {
  children?: ReactNode | undefined
  onClick?: (() => void) | undefined
  variant?: BtnVariant | undefined
  Icon?: LucideIcon | undefined
  disabled?: boolean | undefined
  loading?: boolean | undefined
  type?: 'button' | 'submit' | undefined
  size?: 'sm' | 'md' | undefined
  title?: string | undefined
}): JSX.Element {
  const iconOnly = children === undefined || children === null
  return (
    <button
      type={type}
      title={title}
      aria-label={iconOnly ? title : undefined}
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[3px] font-semibold transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100 ${
        size === 'sm' ? (iconOnly ? 'h-8 w-8' : 'h-8 px-2.5 text-[12px]') : iconOnly ? 'h-9 w-9' : 'h-9 px-3.5 text-[13px]'
      } ${BTN[variant]}`}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : Icon ? <Icon className="h-4 w-4" /> : null}
      {children}
    </button>
  )
}

/** iOS-style segmented control. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
}: {
  options: { key: T; label: ReactNode; count?: number | undefined }[]
  value: T
  onChange: (k: T) => void
  size?: 'sm' | 'md' | undefined
}): JSX.Element {
  return (
    <div className="inline-flex rounded-[3px] bg-slate-100 p-1 ring-1 ring-inset ring-slate-200/60">
      {options.map((o) => {
        const active = o.key === value
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            className={`flex items-center gap-1.5 rounded-[3px] font-semibold transition-all ${size === 'sm' ? 'px-2.5 py-1 text-[11.5px]' : 'px-3 py-1.5 text-[12.5px]'} ${
              active ? 'bg-white text-slate-900 shadow-[0_1px_3px_rgba(15,23,42,0.12)]' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            {o.label}
            {o.count !== undefined && o.count > 0 && (
              <span className={`min-w-[18px] rounded-[3px] px-1.5 text-center text-[10.5px] leading-[18px] ${active ? 'bg-[#3366FF] text-white' : 'bg-slate-200 text-slate-600'}`}>{o.count}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export function SearchInput({ value, onChange, placeholder, className = 'w-56' }: { value: string; onChange: (v: string) => void; placeholder: string; className?: string | undefined }): JSX.Element {
  return (
    <label className={`relative block ${className}`}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input className={`${INPUT} pl-9`} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </label>
  )
}

const STATUS_PILL: Record<string, { cls: string; dot: string }> = {
  APPROVED: { cls: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15', dot: 'bg-emerald-500' },
  PUBLISHED: { cls: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15', dot: 'bg-emerald-500' },
  PENDING: { cls: 'bg-amber-50 text-amber-700 ring-amber-600/20', dot: 'bg-amber-500' },
  PENDING_PEER: { cls: 'bg-sky-50 text-sky-700 ring-sky-600/15', dot: 'bg-sky-500' },
  PENDING_APPROVAL: { cls: 'bg-amber-50 text-amber-700 ring-amber-600/20', dot: 'bg-amber-500' },
  DRAFT: { cls: 'bg-indigo-50 text-indigo-700 ring-indigo-600/15', dot: 'bg-indigo-500' },
  DENIED: { cls: 'bg-rose-50 text-rose-700 ring-rose-600/15', dot: 'bg-rose-500' },
  DECLINED: { cls: 'bg-rose-50 text-rose-700 ring-rose-600/15', dot: 'bg-rose-500' },
}

export function StatusBadge({ status, label }: { status: string; label?: string | undefined }): JSX.Element {
  const s = STATUS_PILL[status] ?? { cls: 'bg-slate-100 text-slate-600 ring-slate-500/15', dot: 'bg-slate-400' }
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-[3px] px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${s.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {label ?? prettyStatus(status)}
    </span>
  )
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

const AVATAR_COLORS = [
  'bg-sky-100 text-sky-700', 'bg-violet-100 text-violet-700', 'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-800', 'bg-rose-100 text-rose-700', 'bg-indigo-100 text-indigo-700', 'bg-teal-100 text-teal-700',
]

export function initials(name: string): string {
  const parts = name.replace(/^Ext\s+/i, '#').trim().split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '?') + (parts.length > 1 ? parts[parts.length - 1]![0] : '')).toUpperCase()
}

export function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' | undefined }): JSX.Element {
  let h = 0
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  const dim = size === 'sm' ? 'h-7 w-7 text-[10.5px]' : size === 'lg' ? 'h-10 w-10 text-[13px]' : 'h-8 w-8 text-[11.5px]'
  return (
    <span className={`inline-flex shrink-0 items-center justify-center rounded-[3px] font-semibold ${dim} ${AVATAR_COLORS[h % AVATAR_COLORS.length]}`} aria-hidden="true">
      {initials(name)}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Forms / overlays
// ---------------------------------------------------------------------------

export function Field({ label, hint, children, span = 1 }: { label: string; hint?: string | undefined; children: ReactNode; span?: 1 | 2 }): JSX.Element {
  return (
    <label className={`flex flex-col gap-1.5 ${span === 2 ? 'sm:col-span-2' : ''}`}>
      <span className="text-[12px] font-medium text-slate-600">{label}</span>
      {children}
      {hint && <span className="text-[11.5px] text-slate-400">{hint}</span>}
    </label>
  )
}

export function Modal({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 'max-w-xl',
}: {
  open: boolean
  title: string
  subtitle?: string | undefined
  onClose: () => void
  children: ReactNode
  footer?: ReactNode | undefined
  width?: string | undefined
}): JSX.Element | null {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center overflow-y-auto bg-slate-900/40 p-4 backdrop-blur-[2px]" onMouseDown={onClose}>
      <div className={`my-auto flex max-h-[calc(100dvh-2rem)] w-full ${width} flex-col overflow-hidden rounded-[3px] bg-white shadow-[0_24px_64px_-16px_rgba(15,23,42,0.35)] ring-1 ring-slate-200`} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="flex items-start justify-between gap-3 px-6 pb-2 pt-5">
          <div>
            <h2 className="text-[17px] font-semibold tracking-[-0.01em] text-slate-900">{title}</h2>
            {subtitle && <p className="mt-1 text-[12.5px] leading-relaxed text-slate-500">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded-[3px] p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && <footer className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/70 px-6 py-3.5">{footer}</footer>}
      </div>
    </div>
  )
}

/** Underline tabs (kept for pages that host tabs inside a card). */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { key: T; label: string; count?: number }[]
  value: T
  onChange: (k: T) => void
}): JSX.Element {
  return (
    <div className="flex gap-1 border-b border-slate-200">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-semibold transition-colors ${
            value === t.key ? 'border-[#3366FF] text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          {t.label}
          {t.count !== undefined && t.count > 0 && (
            <span className={`min-w-[18px] rounded-[3px] px-1.5 text-center text-[10.5px] leading-[18px] ${value === t.key ? 'bg-[#3366FF] text-white' : 'bg-slate-100 text-slate-600'}`}>{t.count}</span>
          )}
        </button>
      ))}
    </div>
  )
}

export function EmptyState({ Icon, title, message, action }: { Icon: LucideIcon; title: string; message: string; action?: ReactNode | undefined }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <span className="relative flex h-14 w-14 items-center justify-center">
        <span className="absolute inset-0 rounded-[3px] bg-blue-50" />
        <span className="absolute inset-2 rounded-[3px] bg-white shadow-sm ring-1 ring-slate-200/70" />
        <Icon className="relative h-5 w-5 text-[#3366FF]" />
      </span>
      <div>
        <p className="text-[14px] font-semibold text-slate-900">{title}</p>
        <p className="mx-auto mt-1 max-w-sm text-[12.5px] leading-relaxed text-slate-500">{message}</p>
      </div>
      {action && <div className="mt-1">{action}</div>}
    </div>
  )
}

export function LoadingBlock(): JSX.Element {
  return (
    <div className="flex flex-col gap-3 p-5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-10 animate-pulse rounded-[3px] bg-slate-100" style={{ opacity: 1 - i * 0.25 }} />
      ))}
    </div>
  )
}

export function ErrorBanner({ message }: { message: string }): JSX.Element {
  return <div className="rounded-[3px] bg-rose-50 px-4 py-3 text-[13px] text-rose-700 ring-1 ring-inset ring-rose-200">{message}</div>
}

// ---------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------

/**
 * Proportional strip of activity blocks across [spanStart, spanEnd]. Used for
 * a shift chip, the agent's day, and the adherence timeline.
 */
export function TimelineStrip({
  blocks,
  spanStart,
  spanEnd,
  height = 'h-2.5',
  marks,
  nowMs,
}: {
  blocks: { start: string | number; end: string | number; color: string | null; label?: string | undefined }[]
  spanStart: number
  spanEnd: number
  height?: string | undefined
  /** Red overlays, e.g. adherence exceptions. */
  marks?: { start: string | number; end: string | number; label?: string | undefined }[] | undefined
  nowMs?: number | undefined
}): JSX.Element {
  const span = Math.max(1, spanEnd - spanStart)
  const pos = (v: string | number): number => {
    const ms = typeof v === 'number' ? v : Date.parse(v)
    return Math.min(100, Math.max(0, ((ms - spanStart) / span) * 100))
  }
  return (
    <div className={`relative w-full overflow-hidden rounded-[3px] bg-slate-100 ${height}`}>
      {blocks.map((b, i) => (
        <span
          key={i}
          title={b.label}
          className="absolute inset-y-0 border-r border-white/70 last:border-r-0"
          style={{ left: `${pos(b.start)}%`, width: `${Math.max(0.4, pos(b.end) - pos(b.start))}%`, background: b.color ?? '#3366FF' }}
        />
      ))}
      {marks?.map((m, i) => (
        <span
          key={`m${i}`}
          title={m.label}
          className="absolute inset-y-0"
          style={{ left: `${pos(m.start)}%`, width: `${Math.max(0.4, pos(m.end) - pos(m.start))}%`, background: 'repeating-linear-gradient(135deg,rgba(244,63,94,0.95) 0 3px,rgba(253,164,175,0.95) 3px 6px)' }}
        />
      ))}
      {nowMs !== undefined && nowMs > spanStart && nowMs < spanEnd && (
        <span className="absolute inset-y-0 w-[2px] bg-slate-900 shadow-[0_0_0_2px_rgba(255,255,255,0.8)]" style={{ left: `${pos(nowMs)}%` }} />
      )}
    </div>
  )
}

export function Legend({ items }: { items: { label: string; color: string }[] }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {items.map((i) => (
        <span key={i.label} className="inline-flex items-center gap-1.5 rounded-[3px] bg-slate-50 px-2 py-0.5 text-[11.5px] font-medium text-slate-600 ring-1 ring-inset ring-slate-200/70">
          <span className="h-2 w-2 rounded-full" style={{ background: i.color }} />
          {i.label}
        </span>
      ))}
    </div>
  )
}

export function WfmPagination({
  page,
  pageSize,
  total,
  noun = 'agents',
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  onPageSizeChange,
}: {
  page: number
  pageSize: number
  total: number
  noun?: string
  pageSizeOptions?: number[]
  onPageChange: (p: number) => void
  onPageSizeChange: (size: number) => void
}): JSX.Element | null {
  if (total === 0) return null

  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)

  const pages: (number | string)[] = []
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i)
  } else if (page <= 3) {
    pages.push(1, 2, 3, 4, '…', totalPages)
  } else if (page >= totalPages - 2) {
    pages.push(1, '…', totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
  } else {
    pages.push(1, '…', page - 1, page, page + 1, '…', totalPages)
  }

  const navBtn =
    'flex h-7 w-7 items-center justify-center rounded-[3px] border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:pointer-events-none disabled:opacity-40 shadow-2xs'

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-5 py-3 text-[12px] text-slate-600">
      <div className="flex items-center gap-1.5">
        <span>Showing</span>
        <span className="font-semibold tabular-nums text-slate-900">{start}–{end}</span>
        <span>of</span>
        <span className="font-semibold tabular-nums text-slate-900">{total.toLocaleString()}</span>
        <span>{noun}</span>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-[11.5px] text-slate-500">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-7 rounded-[3px] border border-slate-200 bg-white px-2 text-[12px] font-medium text-slate-700 shadow-2xs focus:border-[#3366FF] focus:outline-hidden"
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            className={navBtn}
            title="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {pages.map((p, i) =>
            p === '…' ? (
              <span key={`gap-${i}`} className="px-1 text-slate-300">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p as number)}
                className={`h-7 min-w-[28px] rounded-[3px] border px-2 font-mono text-[11.5px] font-semibold transition-colors ${
                  page === p
                    ? 'border-[#3366FF] bg-[#3366FF] text-white shadow-2xs'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 shadow-2xs'
                }`}
              >
                {p}
              </button>
            ),
          )}

          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className={navBtn}
            title="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
